import {defineStore} from "pinia";
import Extension, {ExtensionIndexParams, ExtensionType, ProgramExtension} from "@/core/interfaces/extension/Extension";
import {extensionApi} from "@/container";

interface ExtensionStore {
    /** List of extensions */
    extensions: Extension[] | null;
    /** Loading state */
    loading: boolean;
    /** Refreshing state */
    refreshing: boolean;
    /**
     * When the extensions were last received. A failed request answers an
     * empty list, so an empty answer is not counted as received.
     */
    receivedAt: number | null;
}

export const useExtensionStore = defineStore('extension', {
    state: (): ExtensionStore => ({
        extensions: null,
        loading: false,
        refreshing: false,
        receivedAt: null
    }),

    actions: {
        /**
         * Load the extensions from the API.
         */
        async loadExtensions() {
            this.loading = true;
            try {
                this.setExtensions(await extensionApi()
                    .index({types: [ExtensionType.PROGRAM_BUNDLE, ExtensionType.PACK]}));
            } finally {
                this.loading = false;
            }
        },
        /**
         * Refresh the extensions from the API.
         */
        async refreshExtensions(params: ExtensionIndexParams = {}) {
            this.refreshing = true;
            this.setExtensions(await extensionApi()
                .index({types: [ExtensionType.PROGRAM_BUNDLE, ExtensionType.PACK], ...params}));
            this.refreshing = false;
        },
        /**
         * Keep the extensions read from the API.
         */
        setExtensions(extensions: Extension[]) {
            this.extensions = extensions;
            this.receivedAt = extensions.length ? Date.now() : null;
        },
        /**
         * Set the enabled state of an extension in the store by its id.
         */
        setExtensionEnabled(extensionId: number, enabled: boolean) {
            const extension = this.extensions?.find(item => item.id === extensionId);

            if (extension) {
                extension.enabled = enabled;
            }
        },
        /**
         * Enable an extension and reflect the new state in the store.
         */
        async enableExtension(extensionId: number) {
            await extensionApi().enable(extensionId);
            this.setExtensionEnabled(extensionId, true);
        },
        /**
         * Disable an extension and reflect the new state in the store.
         */
        async disableExtension(extensionId: number) {
            await extensionApi().disable(extensionId);
            this.setExtensionEnabled(extensionId, false);
        }
    },

    getters: {
        /** Returns the enabled extensions */
        enabledExtensions(state): Extension[] {
            return state.extensions?.filter(extension => extension.enabled) || [];
        },
        /** Returns the enabled program extensions */
        enabledProgramExtensions(): ProgramExtension[] {
            return this.enabledExtensions.filter(extension => {
                return extension.extendable_type === ExtensionType.PROGRAM_BUNDLE;
            }) as ProgramExtension[];
        },
        /**
         * Return the program extensions.
         */
        programExtensions(): ProgramExtension[] {
            return (this.extensions?.filter(extension => {
                return extension.extendable_type === ExtensionType.PROGRAM_BUNDLE;
            }) ?? []) as ProgramExtension[];
        },

        /** Returns the extension by slug. */
        extensionBySlug(state): (slug: string) => Extension | undefined {
            return (slug: string): Extension | undefined => {
                return state.extensions?.find(extension => extension.slug === slug);
            };
        },
        /**
         * Get the enabled extensions by subscription plan.
         */
        enabledExtensionsBySubscriptionPlan(): (subscriptionPlanId: number) => Extension[] | undefined {
            return (subscriptionPlanId: number): Extension[] | undefined => {
                return this.enabledExtensions.filter(extension => {
                    return extension.subscription_plan_id === subscriptionPlanId;
                });
            };
        },

        /**
         * Get the extensions by subscription plan.
         */
        extensionsBySubscriptionPlan(): (subscriptionPlanId: number) => Extension[] {
            return (subscriptionPlanId: number): Extension[] => {
                return (this.extensions ?? []).filter(extension => {
                    return extension.subscription_plan_id === subscriptionPlanId;
                });
            };
        }
    }
});
